import { useState, useEffect } from 'react';
import Sidebar from '../components/Sidebar';
import DashboardHeader from '../components/DashboardHeader';
import TransactionList from './components/TransactionList';
import AddTransactionModal from './components/AddTransactionModal';
import EditTransactionModal from './components/EditTransactionModal';
import FinanceCharts from './components/FinanceCharts';

const STORAGE_KEY = 'transactions';

export default function Finance() {
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState<any>(null);
  const [filterType, setFilterType] = useState('all');
  const [transactions, setTransactions] = useState<any[]>([]);

  // Load transactions from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        setTransactions(JSON.parse(stored));
      } catch (error) {
        console.error('Error loading transactions:', error);
        const initialData = getInitialTransactions();
        setTransactions(initialData);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(initialData));
      }
    } else {
      const initialData = getInitialTransactions();
      setTransactions(initialData);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(initialData));
    }
  }, []);

  const getInitialTransactions = () => [
    {
      id: 1,
      type: 'income',
      category: 'Product Sales',
      description: 'Moringa Powder - Bulk Order',
      amount: 125000,
      date: '2024-01-15',
      paymentMethod: 'Bank Transfer',
    },
    {
      id: 2,
      type: 'expense',
      category: 'Raw Materials',
      description: 'Moringa Leaves Purchase',
      amount: 45000,
      date: '2024-01-14',
      paymentMethod: 'Cash',
    },
    {
      id: 3,
      type: 'funds',
      category: 'Investment',
      description: 'Capital Investment - Q1 2024',
      amount: 250000,
      date: '2024-01-12',
      paymentMethod: 'Bank Transfer',
    },
    {
      id: 4,
      type: 'income',
      category: 'Farming',
      description: 'Moringa Crop Harvest Sale',
      amount: 85000,
      date: '2024-01-10',
      paymentMethod: 'Online Payment',
    },
    {
      id: 5,
      type: 'expense',
      category: 'Utilities',
      description: 'Electricity Bill - December',
      amount: 12000,
      date: '2024-01-08',
      paymentMethod: 'Online Payment',
    },
    {
      id: 6,
      type: 'expense',
      category: 'Salaries',
      description: 'Staff Salaries - December',
      amount: 180000,
      date: '2024-01-05',
      paymentMethod: 'Bank Transfer',
    },
    {
      id: 7,
      type: 'expense',
      category: 'Farming',
      description: 'Seeds and Fertilizers',
      amount: 35000,
      date: '2024-01-03',
      paymentMethod: 'Cash',
    },
    {
      id: 8,
      type: 'expense',
      category: 'Marketing',
      description: 'Social Media Advertising',
      amount: 25000,
      date: '2024-01-02',
      paymentMethod: 'Credit Card',
    },
  ];

  const handleAddTransaction = (transaction: any) => {
    const newTransaction = {
      ...transaction,
      id: Date.now(),
    };
    const updatedTransactions = [...transactions, newTransaction];
    setTransactions(updatedTransactions);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedTransactions));
    setShowAddModal(false);
    window.dispatchEvent(new Event('transactionsUpdated'));
  };

  const handleEditTransaction = (transaction: any) => {
    setEditingTransaction(transaction);
    setShowEditModal(true);
  };

  const handleUpdateTransaction = (updatedTransaction: any) => {
    const updatedTransactions = transactions.map(t =>
      t.id === updatedTransaction.id ? updatedTransaction : t
    );
    setTransactions(updatedTransactions);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedTransactions));
    setShowEditModal(false);
    setEditingTransaction(null);
    window.dispatchEvent(new Event('transactionsUpdated'));
  };

  const handleDeleteTransaction = (id: number) => {
    if (window.confirm('Are you sure you want to delete this transaction?')) {
      const updatedTransactions = transactions.filter(t => t.id !== id);
      setTransactions(updatedTransactions);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedTransactions));
      window.dispatchEvent(new Event('transactionsUpdated'));
    }
  };

  // Calculate totals
  const totalIncome = transactions
    .filter(t => t.type === 'income')
    .reduce((sum, t) => sum + t.amount, 0);

  const totalExpenses = transactions
    .filter(t => t.type === 'expense')
    .reduce((sum, t) => sum + t.amount, 0);

  const totalFunds = transactions
    .filter(t => t.type === 'funds')
    .reduce((sum, t) => sum + t.amount, 0);

  const netBalance = totalIncome - totalExpenses;

  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 flex flex-col">
        <DashboardHeader />
        <main className="flex-1 p-8">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-8">
              <h1 className="text-3xl font-bold text-gray-900">Finance Management</h1>
              <button
                onClick={() => setShowAddModal(true)}
                className="px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2"
              >
                <i className="ri-add-line w-5 h-5 flex items-center justify-center"></i>
                Add Transaction
              </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 mb-8">
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-green-50 rounded-lg flex items-center justify-center">
                    <i className="ri-arrow-up-line text-2xl text-green-600 w-6 h-6 flex items-center justify-center"></i>
                  </div>
                </div>
                <h3 className="text-sm text-gray-600 mb-1">Total Income</h3>
                <p className="text-2xl font-bold text-gray-900">₹{totalIncome.toLocaleString()}</p>
                <p className="text-xs text-green-600 mt-2">{transactions.filter(t => t.type === 'income').length} transactions</p>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-red-50 rounded-lg flex items-center justify-center">
                    <i className="ri-arrow-down-line text-2xl text-red-600 w-6 h-6 flex items-center justify-center"></i>
                  </div>
                </div>
                <h3 className="text-sm text-gray-600 mb-1">Total Expenses</h3>
                <p className="text-2xl font-bold text-gray-900">₹{totalExpenses.toLocaleString()}</p>
                <p className="text-xs text-red-600 mt-2">{transactions.filter(t => t.type === 'expense').length} transactions</p>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-teal-50 rounded-lg flex items-center justify-center">
                    <i className="ri-wallet-line text-2xl text-teal-600 w-6 h-6 flex items-center justify-center"></i>
                  </div>
                </div>
                <h3 className="text-sm text-gray-600 mb-1">Net Balance</h3>
                <p className={`text-2xl font-bold ${netBalance >= 0 ? 'text-gray-900' : 'text-red-600'}`}>
                  ₹{netBalance.toLocaleString()}
                </p>
                <p className={`text-xs mt-2 ${netBalance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {netBalance >= 0 ? 'Positive' : 'Negative'} balance
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center">
                    <i className="ri-funds-line text-2xl text-blue-600 w-6 h-6 flex items-center justify-center"></i>
                  </div>
                </div>
                <h3 className="text-sm text-gray-600 mb-1">Funds Added</h3>
                <p className="text-2xl font-bold text-gray-900">₹{totalFunds.toLocaleString()}</p>
                <p className="text-xs text-blue-600 mt-2">{transactions.filter(t => t.type === 'funds').length} transactions</p>
              </div>
            </div>

            <FinanceCharts />

            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 mt-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900">Transaction History</h2>
                <div className="flex items-center gap-3">
                  <select
                    value={filterType}
                    onChange={(e) => setFilterType(e.target.value)}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 cursor-pointer"
                  >
                    <option value="all">All Transactions</option>
                    <option value="income">Income Only</option>
                    <option value="expense">Expenses Only</option>
                    <option value="funds">Funds Added</option>
                  </select>
                </div>
              </div>
              <TransactionList 
                filterType={filterType} 
                transactions={transactions}
                onEdit={handleEditTransaction}
                onDelete={handleDeleteTransaction}
              />
            </div>
          </div>
        </main>
      </div>

      {showAddModal && (
        <AddTransactionModal 
          onClose={() => setShowAddModal(false)} 
          onAdd={handleAddTransaction}
        />
      )}

      {showEditModal && editingTransaction && (
        <EditTransactionModal
          transaction={editingTransaction}
          onClose={() => {
            setShowEditModal(false);
            setEditingTransaction(null);
          }}
          onUpdate={handleUpdateTransaction}
        />
      )}
    </div>
  );
}
